import React, {Component} from 'react';
import css from './DropDown.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import onClickOutside from 'react-onclickoutside';


/**
 * @class
 *
 * Dropdown component. Clicking on the button opens a list of options, and selecting an option from the list triggers a callback.
 *
 * @prop onDropdownSelect {function}: Handler for when a dropdown option is selected. The selected value {string} should be passed into the callback.
 * @prop default {object}: The default selected option.
 * @prop options {array}: An array of objects. Each object in array has the following properties:
 *      label {string}: Displayed text/label for option.
 *      value {string}: Actual value of option.
 *
 * @example
 *  // markup:
 *  <DropDown
 *      options={this.dropDownOptions}
 *      onDropdownSelect={(val) => this.handleDropdownOnChange(val)}
 *      default={{value: 'foo', label: 'bar'}}
 *  />
 *
 *  // onDropdownSelect handler:
 *  // n.b. `val` is a string (equal to the `value` property from the list of options we passed):
 *  handleDropdownOnChange = (val) => {
 *      // do something -- e.g.:
 *      this.setState({ selectedValue: val });
 *  };
 */
const DropDown = class extends Component {
    // ---------- state: ----------
    state = {
        isActive: false,
        selected: {},
    };

    // ---------- life cycle methods: ----------
    componentDidMount() {
        // update selected value to default, if a default is set and has correct properties (value and label):
        if (this.props.default && this.props.default.value && this.props.default.label) {
            this.setState({ selected: this.props.default });
        } else {
            this.setState({ selected: this.props.options[0] });
        }
    }

    // ---------- methods: ----------
    /**
     * @method
     * @methodOf DropDown
     *
     * Handler for when user clicks outside the component -- hides the dropdown.
     *
     * This is an out-of-the-box handler provided by rect-onclickoutside <https://www.npmjs.com/package/react-onclickoutside#es6-class-component>.
     * Anytime the user clicks outside the component, the handler is called.
     */
    handleClickOutside = () => {
        this.setState({isActive: false});
    };

    /**
     * @method
     * @methodOf DropDown
     *
     * Handler for when the button element of the component is clicked -- hides/shows the dropdown.
     */
    toggleDropDownOnClick = () => {
        this.setState({
            isActive: !this.state.isActive
        });
    };

    /**
     * @method
     * @methodOf DropDown
     *
     * Handler for when user clicks on a dropdown option -- the `onDropdownSelect` prop is fired
     * with the selected option as its argument.
     *
     * Dropdown is closed after an option is selected.
     *
     * @param option {object}: The selected option from the dropdown.
     */
    optionOnClick = (option) => {
        this.props.onDropdownSelect(option.value);

        this.setState({
            selected: option
        });

        // close drop-down:
        this.toggleDropDownOnClick();
    };

    render() {
        return(
            <div className={css.DropDown}>
                <button className="btn btn-primary w-100" type="button" onClick={() => this.toggleDropDownOnClick()}>
                    <span>{ this.state.selected.label }</span>
                    <FontAwesomeIcon className="ml-2" icon={faChevronDown} />
                </button>
                <ul className={css.DropDown__List} style={{ display: this.state.isActive ? "block" : "none" }}>
                    {
                        this.props.options.map((option, index) => {
                                return (
                                    <li
                                        className={css.DropDown__ListItem}
                                        key={index}
                                        onClick={ () => this.optionOnClick(option) }
                                    >
                                        { option.label }
                                    </li>
                                );
                            })
                    }
                </ul>
            </div>
        );
    }
}

// In order to use react-onclickoutside, we wrap the component -- see <https://www.npmjs.com/package/react-onclickoutside#usage>:
export default onClickOutside(DropDown);
